"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Crown,
  Diamond,
  Sparkles,
  Play,
  ShieldCheck,
  Truck,
  CreditCard,
  Headset,
} from "lucide-react";
import { useUI } from "@/lib/store";
import Image from "next/image";

/* ═══════════════════════════════════════════════════════════════════
   LE TOP DU SITE DÉFILE — 8 Slides Vedettes (comme sur le site d'origine)
   Rotation automatique toutes les 7 secondes + navigation manuelle.
   Ordre demandé par le client :
   1. Forfaits 5G Illimité            5. Galaxy S25 Ultra
   2. Carte SIM Intelligente (IA)     6. Setup Gaming Elite
   3. Smart TV 8K OLED                7. e-SIM Sans Frontières
   4. Moniteur OLED 4K                8. Sénégal Connecté
   ═══════════════════════════════════════════════════════════════════ */

type CtaKind = "scroll" | "forfaits";

interface HeroSlide {
  id: string;
  badge: string;
  title: string;
  subtitle: string;
  description: string;
  price: string;
  pricePrefix?: string;
  image: string;
  accent: string;
  cta: string;
  ctaKind: CtaKind;
  ctaTarget: string;
  cta2: string;
  cta2Kind: CtaKind;
  cta2Target: string;
}

const slides: HeroSlide[] = [
  {
    id: "forfaits-5g",
    badge: "OFFRES EXCLUSIVES",
    title: "Forfaits 5G Illimité",
    subtitle: "La Connexion Sans Limites",
    description:
      "5G Ultra, appels illimités, roaming dans 120 pays. Le forfait le plus prestigieux d'Afrique de l'Ouest, exclusivité Senegal Telecom.",
    pricePrefix: "À partir de",
    price: "35 000 FCFA/mois",
    image: "/images/luxury/offres-hero-premium.png",
    accent: "#FFD700",
    cta: "Voir les Offres",
    ctaKind: "scroll",
    ctaTarget: "promos",
    cta2: "Comparer les Forfaits",
    cta2Kind: "forfaits",
    cta2Target: "",
  },
  {
    id: "carte-sim-ia",
    badge: "AI SIM PREMIUM",
    title: "Carte SIM Intelligente",
    subtitle: "L'IA au Cœur de la SIM",
    description:
      "La première carte SIM dotée d'intelligence artificielle. Gestion automatique du forfait, roaming prédictif et sécurité biométrique intégrée.",
    pricePrefix: "À partir de",
    price: "15 000 FCFA",
    image: "/images/luxury/asim.png",
    accent: "#FFD700",
    cta: "Activer AI SIM",
    ctaKind: "forfaits",
    ctaTarget: "",
    cta2: "Voir les Détails",
    cta2Kind: "scroll",
    cta2Target: "mondes",
  },
  {
    id: "smart-tv-8k",
    badge: "ÉDITION LIMITÉE",
    title: "Smart TV 8K OLED",
    subtitle: "Le Cinéma au Palais",
    description:
      "OLED 8K 85 pouces, processeur AI Neural, Dolby Atmos 7.1.4. Une œuvre d'art pour votre salon premium.",
    price: "4 999 000 FCFA",
    image: "/images/luxury/tv-luxury-room.png",
    accent: "#FFD700",
    cta: "Explorer",
    ctaKind: "scroll",
    ctaTarget: "merveilles",
    cta2: "Voir en Showroom",
    cta2Kind: "scroll",
    cta2Target: "merveilles",
  },
  {
    id: "moniteur-oled-4k",
    badge: "CRÉATEUR PRO",
    title: "Moniteur OLED 4K",
    subtitle: "La Précision Absolue",
    description:
      "OLED 4K 32 pouces, 240Hz, HDR1000, calibration Pro. Le moniteur de référence pour designers, créateurs et professionnels de l'image.",
    price: "1 899 000 FCFA",
    image: "/images/luxury/moniteur.png",
    accent: "#00C774",
    cta: "Découvrir",
    ctaKind: "scroll",
    ctaTarget: "nouveautes",
    cta2: "Configurer",
    cta2Kind: "scroll",
    cta2Target: "merveilles",
  },
  {
    id: "galaxy-s25-ultra",
    badge: "PLATINIUM EXCLUSIF",
    title: "Galaxy S25 Ultra",
    subtitle: "L'Intelligence Absolue",
    description:
      "Titanium. 200MP. AI Pro. Le smartphone le plus prestigieux d'Afrique, exclusivement chez Senegal Telecom avec garantie 3 ans.",
    price: "1 299 000 FCFA",
    image: "/images/luxury/platinum-phone.png",
    accent: "#00C774",
    cta: "Découvrir",
    ctaKind: "scroll",
    ctaTarget: "nouveautes",
    cta2: "Pré-commander",
    cta2Kind: "scroll",
    cta2Target: "presidentiel",
  },
  {
    id: "setup-gaming-elite",
    badge: "GAMER PRO",
    title: "Setup Gaming Elite",
    subtitle: "La Puissance Absolue",
    description:
      "PC RTX 5090, Monitor OLED 240Hz, clavier méca et casque 7.1. Le setup ultime pour les champions du gaming africain.",
    price: "4 999 000 FCFA",
    image: "/images/luxury/gaming-luxury.png",
    accent: "#00C774",
    cta: "Découvrir le Setup",
    ctaKind: "scroll",
    ctaTarget: "merveilles",
    cta2: "Personnaliser",
    cta2Kind: "scroll",
    cta2Target: "nouveautes",
  },
  {
    id: "esim-sans-frontieres",
    badge: "e-SIM PREMIUM",
    title: "e-SIM Sans Frontières",
    subtitle: "Le Mobile Universel",
    description:
      "Activez votre ligne instantanément, changez d'opérateur en un clic. L'e-SIM révolutionne la mobilité — disponible exclusivement chez ST.",
    pricePrefix: "À partir de",
    price: "10 000 FCFA",
    image: "/images/luxury/mobile-esim-premium.png",
    accent: "#FF4D5E",
    cta: "Activer e-SIM",
    ctaKind: "forfaits",
    ctaTarget: "",
    cta2: "En savoir plus",
    cta2Kind: "scroll",
    cta2Target: "mondes",
  },
  {
    id: "senegal-connecte",
    badge: "TERANGA DIGITALE",
    title: "Sénégal Connecté",
    subtitle: "L'Excellence au Service de la Nation",
    description:
      "Du drapeau tricolore à la révolution numérique. Senegal Telecom accompagne le Sénégal vers un avenir 100% connecté et inclusif.",
    price: "Ensemble",
    image: "/images/luxury/drapeau-senegal.png",
    accent: "#FF4D5E",
    cta: "Notre Vision",
    ctaKind: "scroll",
    ctaTarget: "excellence",
    cta2: "Découvrir ST",
    cta2Kind: "scroll",
    cta2Target: "mondes",
  },
];

const perks = [
  { icon: Truck, title: "Livraison Concierge", sub: "Blanche & Prioritaire" },
  { icon: ShieldCheck, title: "Garantie Platinium", sub: "3 ans extension" },
  { icon: CreditCard, title: "Paiement 6x Flex", sub: "Sans frais" },
  { icon: Headset, title: "Concierge 24/7", sub: "WhatsApp & Chat" },
];

const ROTATE_MS = 7000; // rotation automatique : 7 secondes
const PAUSE_MS = 12000; // pause après navigation manuelle : 12 secondes

export function Hero() {
  const openForfaits = useUI((s) => s.openForfaits);
  const [index, setIndex] = useState(0);
  const [auto, setAuto] = useState(true);
  const pauseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* Rotation automatique toutes les 7 s */
  useEffect(() => {
    if (!auto) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % slides.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [auto]);

  /* Navigation manuelle : met la rotation en pause 12 s */
  const goTo = useCallback((i: number) => {
    setIndex(((i % slides.length) + slides.length) % slides.length);
    setAuto(false);
    if (pauseTimer.current) clearTimeout(pauseTimer.current);
    pauseTimer.current = setTimeout(() => setAuto(true), PAUSE_MS);
  }, []);

  /* Exécution des CTA — tous les boutons sont fonctionnels */
  const runCta = useCallback(
    (kind: CtaKind, target: string) => {
      if (kind === "forfaits") {
        openForfaits();
        return;
      }
      document.getElementById(target)?.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    [openForfaits]
  );

  const slide = slides[index];

  return (
    <section id="hero" className="relative overflow-hidden bg-black" aria-roledescription="carrousel" aria-label="Offres vedettes">
      {/* Fond showroom d'origine — voile luxueux derrière tout le carrousel */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <Image
          src="/images/luxury/hero-showroom.png"
          alt=""
          fill
          priority
          className="object-cover opacity-[0.12]"
          sizes="100vw"
        />
      </div>
      {/* Texture dorée d'origine — coin supérieur droit */}
      <div className="absolute inset-0 z-[1] pointer-events-none overflow-hidden">
        <Image
          src="/images/luxury/gold-texture.png"
          alt=""
          fill
          className="absolute top-0 right-0 w-1/3 h-1/2 object-cover opacity-[0.06] mix-blend-overlay"
          sizes="33vw"
        />
      </div>

      {/* Liserés tricolores haut / bas */}
      <div className="absolute top-0 left-0 right-0 z-30 h-1 bg-gradient-to-r from-[#006B3F] via-[#FFD700] to-[#CE1126]" />
      <div className="absolute bottom-0 left-0 right-0 z-30 h-1 bg-gradient-to-r from-[#CE1126] via-[#FFD700] to-[#006B3F]" />

      {/* Particules flottantes aux couleurs du Sénégal — vert · or · rouge */}
      <div className="absolute inset-0 z-[15] pointer-events-none overflow-hidden">
        {Array.from({ length: 20 }).map((_, a) => {
          const senegalColor = a % 3 === 0 ? "#006B3F" : a % 3 === 1 ? "#FFD700" : "#CE1126";
          return (
            <motion.div
              key={a}
              className="absolute rounded-full"
              style={{
                width: `${3 + (a % 3)}px`,
                height: `${3 + (a % 3)}px`,
                left: `${4 + 4.8 * a}%`,
                top: `${12 + (a % 4) * 21}%`,
                background: senegalColor,
                boxShadow: `0 0 ${7 + (a % 3) * 3}px ${senegalColor}`,
              }}
              animate={{
                y: [0, -28 - (a % 5) * 6, 0],
                x: [0, a % 2 === 0 ? 14 : -14, 0],
                opacity: [0.15, 0.7, 0.15],
                scale: [1, 1.35, 1],
              }}
              transition={{
                duration: 5 + 0.4 * a,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 0.25 * a,
              }}
            />
          );
        })}
      </div>

      {/* Reflet doré balayant */}
      <motion.div
        initial={{ x: "-120%" }}
        animate={{ x: "320%" }}
        transition={{ duration: 3, repeat: Infinity, repeatDelay: 5, ease: "easeInOut" }}
        className="pointer-events-none absolute inset-y-0 w-1/3 z-20 bg-gradient-to-r from-transparent via-[#FFD700]/[0.07] to-transparent skew-x-12"
      />

      {/* ── Zone carrousel (contexte de position pour la navigation) ── */}
      <div className="relative">
      <AnimatePresence mode="wait">
        <motion.div
          key={slide.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7 }}
          className="relative"
        >
          {/* Fond de la slide — effet Ken Burns */}
          <div className="absolute inset-0">
            <motion.div
              initial={{ scale: 1.12, opacity: 0 }}
              animate={{ scale: 1, opacity: 0.45 }}
              transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
              className="absolute inset-0"
            >
              <Image
                src={slide.image}
                alt={slide.title}
                fill
                priority={index === 0}
                className="object-cover"
                sizes="100vw"
              />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-r from-black/[0.96] via-black/80 to-black/40" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40" />
            <div
              className="absolute inset-0"
              style={{ background: "radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.65) 100%)" }}
            />
          </div>

          {/* Contenu */}
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-14 pb-16 sm:pt-20 sm:pb-20">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center min-h-[62vh] sm:min-h-[58vh]">
              {/* Colonne texte */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.25, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="space-y-5 sm:space-y-6"
              >
                <motion.div
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35 }}
                  className="inline-flex items-center gap-2.5 px-5 sm:px-6 py-2.5 rounded-full text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase backdrop-blur-md shadow-xl"
                  style={{
                    background: `${slide.accent}18`,
                    color: slide.accent,
                    border: `1px solid ${slide.accent}40`,
                    boxShadow: `0 8px 30px ${slide.accent}20`,
                  }}
                >
                  <Crown className="w-3.5 h-3.5" />
                  {slide.badge}
                  <Diamond className="w-2.5 h-2.5 opacity-50" />
                </motion.div>

                <motion.h1
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.45, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                  className="text-4xl sm:text-6xl xl:text-7xl font-black text-white leading-[0.95] tracking-tight drop-shadow-[0_0_40px_rgba(255,255,255,0.08)]"
                >
                  {slide.title}
                </motion.h1>

                <div className="flex items-center gap-3">
                  <div className="h-[2px] w-14 bg-gradient-to-r from-[#FFD700]/50 to-[#FFD700]/20" />
                  <Diamond className="w-2.5 h-2.5 text-[#FFD700]/40 rotate-45" />
                  <div className="h-[2px] w-8 bg-gradient-to-r from-[#FFD700]/20 to-transparent" />
                </div>

                <motion.p
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.55, duration: 0.7 }}
                  className="text-xl sm:text-3xl xl:text-4xl font-extralight tracking-wide"
                  style={{ color: slide.accent, textShadow: `0 0 40px ${slide.accent}45` }}
                >
                  {slide.subtitle}
                </motion.p>

                <motion.p
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.62 }}
                  className="text-sm sm:text-base text-white/60 max-w-xl leading-relaxed font-light"
                >
                  {slide.description}
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.68 }}
                  className="inline-flex items-center gap-3 px-6 py-3.5 rounded-2xl bg-white/[0.05] border border-[#FFD700]/15 backdrop-blur-xl shadow-2xl shadow-black/30"
                >
                  <Sparkles className="w-4 h-4 text-[#FFD700]/60 shrink-0" />
                  {slide.pricePrefix && (
                    <span className="text-xs sm:text-sm text-white/50 font-medium tracking-wide">{slide.pricePrefix}</span>
                  )}
                  <span
                    className="text-xl sm:text-3xl font-black tracking-tight"
                    style={{ color: slide.accent, textShadow: `0 0 25px ${slide.accent}40` }}
                  >
                    {slide.price}
                  </span>
                </motion.div>

                {/* CTA — boutons 100% fonctionnels */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.75, duration: 0.7 }}
                  className="flex flex-wrap gap-3 sm:gap-4 pt-2"
                >
                  <button
                    onClick={() => runCta(slide.ctaKind, slide.ctaTarget)}
                    className="group flex items-center gap-2.5 px-7 sm:px-9 py-4 sm:py-4.5 rounded-2xl text-gray-900 font-bold text-sm sm:text-base shadow-2xl hover:scale-[1.04] active:scale-[0.98] transition-all duration-300 cursor-pointer border border-white/20 hover:border-[#FFD700]/50 hover:shadow-[0_0_40px_rgba(255,215,0,0.3)]"
                    style={{ background: `linear-gradient(135deg, ${slide.accent}, ${slide.accent}CC)` }}
                  >
                    {slide.cta}
                    <ChevronRight className="w-5 h-5 group-hover:translate-x-1.5 transition-transform duration-300" />
                  </button>
                  <button
                    onClick={() => runCta(slide.cta2Kind, slide.cta2Target)}
                    className="group flex items-center gap-2.5 px-7 sm:px-9 py-4 sm:py-4.5 rounded-2xl border-2 border-white/20 text-white font-bold text-sm sm:text-base hover:bg-white/[0.08] hover:border-[#FFD700]/40 transition-all duration-300 backdrop-blur-xl cursor-pointer hover:shadow-[0_0_30px_rgba(255,215,0,0.15)]"
                  >
                    <Play className="w-4 h-4" />
                    {slide.cta2}
                  </button>
                </motion.div>
              </motion.div>

              {/* Colonne visuelle (desktop) — cadre rotatif doré */}
              <motion.div
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.45, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="hidden lg:flex items-center justify-center"
              >
                <div className="relative w-[430px] h-[430px]">
                  <div className="absolute inset-[-26px] rounded-[46px] border border-[#FFD700]/15 shadow-[0_0_100px_rgba(255,215,0,0.12)]" />
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 44, repeat: Infinity, ease: "linear" }}
                    className="absolute inset-[-46px] rounded-[56px] border border-dashed border-[#FFD700]/15"
                  />
                  <motion.div
                    animate={{ rotate: -360 }}
                    transition={{ duration: 62, repeat: Infinity, ease: "linear" }}
                    className="absolute inset-[-62px] rounded-[72px] border border-[#FFD700]/10"
                    style={{ borderStyle: "dotted" }}
                  />
                  <div className="relative w-full h-full rounded-[36px] overflow-hidden border-2 border-[#FFD700]/25 shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
                    <Image src={slide.image} alt={slide.title} fill className="object-cover" sizes="430px" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
                    <div
                      className="absolute bottom-4 left-4 right-4 px-4 py-2.5 rounded-xl backdrop-blur-md text-center text-xs font-bold tracking-[0.2em] uppercase"
                      style={{ background: `${slide.accent}22`, color: slide.accent, border: `1px solid ${slide.accent}45` }}
                    >
                      {slide.badge}
                    </div>
                  </div>
                  <Diamond className="absolute -top-3 -left-3 w-4 h-4 text-[#FFD700]/40 animate-pulse" />
                  <Diamond className="absolute -bottom-3 -right-3 w-4 h-4 text-[#FFD700]/40 animate-pulse" />
                </div>
              </motion.div>
            </div>
          </div>

        </motion.div>
      </AnimatePresence>

      {/* Navigation : flèches + pastilles — STABLE, hors transitions */}
      <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 z-30 flex items-center gap-4 sm:gap-6">
        <button
          onClick={() => goTo(index - 1)}
          aria-label="Slide précédente"
          className="p-3 rounded-full bg-white/[0.08] hover:bg-white/15 text-white backdrop-blur-xl border border-[#FFD700]/15 hover:border-[#FFD700]/40 shadow-xl transition-all duration-300 cursor-pointer"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-2.5" role="tablist" aria-label="Choisir la slide">
          {slides.map((s, i) => (
            <button
              key={s.id}
              role="tab"
              aria-selected={i === index}
              aria-label={s.title}
              onClick={() => goTo(i)}
              className={`transition-all duration-500 rounded-full cursor-pointer ${
                i === index
                  ? "w-12 sm:w-16 h-3.5 bg-gradient-to-r from-[#FFD700] to-[#FFA500] shadow-lg shadow-[#FFD700]/40"
                  : "w-3.5 h-3.5 bg-white/25 hover:bg-white/45 border border-white/20"
              }`}
            />
          ))}
        </div>

        <button
          onClick={() => goTo(index + 1)}
          aria-label="Slide suivante"
          className="p-3 rounded-full bg-white/[0.08] hover:bg-white/15 text-white backdrop-blur-xl border border-[#FFD700]/15 hover:border-[#FFD700]/40 shadow-xl transition-all duration-300 cursor-pointer"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Compteur de slides */}
      <div className="absolute top-8 right-6 sm:right-10 z-30 hidden sm:flex items-center gap-2 text-white/50 text-xs font-bold tracking-widest">
        <span className="text-[#FFD700] text-base">{String(index + 1).padStart(2, "0")}</span>
        <span className="w-6 h-[1px] bg-white/25" />
        <span>{String(slides.length).padStart(2, "0")}</span>
      </div>
      </div>

      {/* Perks + teaser présidentiel (statiques sous le carrousel) */}
      <div className="relative z-10 bg-black/60 backdrop-blur-md border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {perks.map((perk) => (
              <div
                key={perk.title}
                className="flex items-center gap-3 p-3.5 rounded-2xl bg-white/[0.05] backdrop-blur-md border border-white/10 hover:border-[#FFD700]/40 transition-colors"
              >
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#FFD700]/20 to-[#FFA500]/10 flex items-center justify-center shrink-0">
                  <perk.icon className="w-4.5 h-4.5 text-[#FFD700]" />
                </div>
                <div className="min-w-0">
                  <p className="text-white font-semibold text-xs truncate">{perk.title}</p>
                  <p className="text-white/50 text-[11px] truncate">{perk.sub}</p>
                </div>
              </div>
            ))}
          </div>

          <a
            href="#presidentiel"
            className="mt-4 flex items-center gap-3 px-5 py-3 rounded-2xl border border-[#FFD700]/30 bg-gradient-to-r from-[#1A1408]/90 to-[#2A1F0E]/90 hover:border-[#FFD700]/60 transition-all group max-w-fit"
          >
            <Crown className="w-5 h-5 text-[#FFD700]" />
            <div>
              <p className="text-[#FFD700] text-xs sm:text-sm font-bold tracking-wide">
                NOUVEAU — Les Merveilles Présidentielles
              </p>
              <p className="text-white/50 text-[11px]">Collection d&apos;État · Éditions numérotées · Garantie Souveraine 5 ans</p>
            </div>
            <ChevronRight className="w-4 h-4 text-[#FFD700] ml-2 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </section>
  );
}
